import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  Channel,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { MessagePayload, getMessaging, getToken, onMessage } from 'firebase/messaging';
import { Observable, catchError, of, skip, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { eventTypeToData, INotificationData, canDisplayAlertEventType } from '../../shared/models/be/notification';
import { IonicColors } from '../../shared/enums/ionicColors.enum';
import { getTimeDifferenceInMs } from '../../shared/utils/transformation.util';
import { LoggerService } from './utils/logger.service';
import { RequestsService } from './utils/requests.service';
import { RingtoneService } from './utils/ringtone.service';
import { SharedService } from './shared.service';
import { ToastService } from './utils/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { MonitoringPreferencesService } from './monitoring-preferences.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly nativePush = PushNotifications;
  private readonly fcmTokenStorageKey = 'FCM_TOKEN';
  private readonly registeredUserStorageKey = 'FCM_REGISTERED_USER_ID';
  private readonly registeredBuildingStorageKey = 'FCM_REGISTERED_BUILDING_ID';
  private readonly registeredTokenStorageKey = 'FCM_REGISTERED_TOKEN';

  public message?: MessagePayload;
  public app: FirebaseApp;

  private listenersRegistered = false;
  private userContextSyncInitialized = false;

  constructor(
    private toastService: ToastService,
    private http: HttpClient,
    private requestsService: RequestsService,
    private ringtoneService: RingtoneService,
    private sharedService: SharedService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
    @Optional() private monitoringPreferences: MonitoringPreferencesService | null = null,
  ) {
    this.app = getApps().length ? getApp() : initializeApp(environment.firebase);
    this.initializeUserContextSync();
    this.sharedService.locallyManagedAlert$.subscribe(alert => {
      if (Capacitor.isNativePlatform()) void this.removeDeliveredAlertNotification(alert.id);
    });
  }

  private async removeDeliveredAlertNotification(alertId: string): Promise<void> {
    try {
      const delivered = await this.nativePush.getDeliveredNotifications();
      // The backend sets Android's tag to the alert ID. The OS notification ID
      // is a different identifier and must be preserved when cancelling it.
      const notifications = delivered.notifications.filter(notification => !notification.groupSummary
        && (notification.tag === String(alertId) || notification.data?.['id'] === String(alertId)));
      if (notifications.length) await this.nativePush.removeDeliveredNotifications({ notifications });
    } catch {
      // A tray cleanup failure must never turn a successful management into an error.
      this.loggerService.warn('Impossibile rimuovere la notifica dell’allerta gestita dalla tendina');
    }
  }

  async checkNotificationPermission() {
    const currentUser = this.requestsService.getCurrentUser();
    if (!currentUser) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const status = await this.nativePush.checkPermissions();

        if (status.receive === 'granted') {
          await this.nativePush.register();
          await this.createCustomChannel();
          return;
        }

        const result = await this.nativePush.requestPermissions();
        if (result.receive === 'granted') {
          this.loggerService.info('Permesso notifiche concesso');
          await this.nativePush.register();
          await this.createCustomChannel();
        } else if (result.receive === 'prompt') {
          this.loggerService.info('Permesso notifiche in attesa di conferma');
        } else {
          this.loggerService.error('Permesso notifiche non consentito');
          this.toastService.showToast('Attenzione, permesso notifiche non consentito');
        }
      } catch (err) {
        this.loggerService.error('Errore durante la verifica dei permessi notifiche');
      }
      return;
    }

    try {
      const messaging = getMessaging(this.app);
      const currentToken = await getToken(messaging, { vapidKey: environment.firebase.vapidKey });

      if (currentToken) {
        this.loggerService.debug(`Token FCM recuperato con successo: ${currentToken}`);
        this.handleFcmTokenReceived(currentToken, currentUser.building?.id);
      } else {
        this.loggerService.error('Errore nel recupero del token FCM');
      }
    } catch (err) {
      this.loggerService.error('Errore nel recupero del token FCM');
      this.toastService.showToast('Autorizzare consenso notifiche!', IonicColors.RED);
    }
  }

  public removeToken(buildingId?: string, token?: string) {
    const registeredContext = this.getRegisteredTokenContext();
    const currentUser = this.requestsService.getCurrentUser();
    const effectiveToken = token || this.getStoredFcmToken() || registeredContext.token;
    const effectiveBuildingId = buildingId || currentUser?.building?.id || registeredContext.buildingId;

    if (!effectiveToken || !effectiveBuildingId) {
      return;
    }

    this.unregisterTokenRequest$(effectiveBuildingId, effectiveToken, true).subscribe({
      error: () => undefined,
    });
  }

  checkLocalNotifications() {
    LocalNotifications.getDeliveredNotifications().then((data) => {
      (data.notifications || []).forEach((notification) => {
        if (notification.tag) {
          this.http.get<INotificationData>(
            `${environment.apiEndpoint}/notifications/${notification.tag}`
          ).pipe(
            take(1),
            tap((alert) => {
              if (alert.managedAt || !canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role)) {
                LocalNotifications.removeDeliveredNotifications({ notifications: [notification] });
              }
            })
          ).subscribe({ error: error => {
            if ([403, 404].includes(error.status)) {
              LocalNotifications.removeDeliveredNotifications({ notifications: [notification] });
            }
          } });
        } else {
          LocalNotifications.removeDeliveredNotifications({ notifications: [notification] });
        }
      });
    });
  }

  public listenForNotifications() {
    if (this.listenersRegistered) {
      return;
    }

    this.listenersRegistered = true;
    this.loggerService.info('Registrando gli ascoltatori delle notifiche...');

    if (Capacitor.isNativePlatform()) {
      this.nativePush.addListener('registration', (token: Token) => {
        const currentUser = this.requestsService.getCurrentUser();

        if (!token.value) {
          this.loggerService.error('Errore nel recupero del token FCM');
          return;
        }

        this.loggerService.info(`Token FCM recuperato con successo: ${token.value}`);
        this.handleFcmTokenReceived(token.value, currentUser?.building?.id);
      });

      this.nativePush.addListener('registrationError', () => {
        this.loggerService.error('Errore registrazione token FCM');
        this.toastService.showToast('Errore tecnico interno - NOTIFICHE', IonicColors.RED);
      });

      this.nativePush.addListener('pushNotificationReceived', (payload: PushNotificationSchema) => {
        this.handleIncomingNotification(payload);
      });

      this.nativePush.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
        this.loggerService.info(`Notification action performed: ${notification}`);
      });

      return;
    }

    const messaging = getMessaging(this.app);
    onMessage(messaging, async (payload) => {
      this.handleIncomingNotification(payload);
    });
  }

  async createCustomChannel() {
    const channel: Channel = {
      id: 'generic_alert',
      name: 'Avvisi Generici',
      description: 'Canale per gli avvisi standard con suono custom',
      importance: 5,
      sound: 'notification_sound'
    };

    try {
      await this.nativePush.createChannel(channel);
      this.loggerService.info('Canale di notifica creato con successo.');
    } catch (e: any) {
      this.loggerService.error(`Errore nella creazione del canale di notifica: ${e.message}`);
    }
  }

  private initializeUserContextSync() {
    if (this.userContextSyncInitialized) {
      return;
    }

    this.userContextSyncInitialized = true;
    this.requestsService.currentUser$.pipe(
      skip(1),
      tap((currentUser) => {
        const userId = currentUser?.id;
        const buildingId = currentUser?.building?.id;
        const token = this.getStoredFcmToken();

        if (userId && buildingId && token) {
          this.syncTokenRegistration(userId, buildingId, token);
        }
      })
    ).subscribe();
  }

  private handleFcmTokenReceived(token: string, buildingId?: string) {
    const userId = this.requestsService.getCurrentUser()?.id;
    const previousToken = this.getStoredFcmToken();

    if (!previousToken || previousToken !== token) {
      localStorage.setItem(this.fcmTokenStorageKey, token);
    }

    if (userId && buildingId) {
      this.syncTokenRegistration(userId, buildingId, token);
    }
  }

  private syncTokenRegistration(userId: number, buildingId: string, token: string) {
    const registeredContext = this.getRegisteredTokenContext();

    if (
      registeredContext.userId === userId &&
      registeredContext.buildingId === buildingId &&
      registeredContext.token === token
    ) {
      return;
    }

    let cleanup$: Observable<unknown> = of(null);
    if (
      registeredContext.buildingId &&
      registeredContext.token &&
      (registeredContext.buildingId !== buildingId || registeredContext.token !== token)
    ) {
      cleanup$ = this.unregisterTokenRequest$(registeredContext.buildingId, registeredContext.token, false).pipe(
        catchError(() => {
          this.loggerService.warn('Impossibile rimuovere la vecchia associazione token/building, procedo comunque con la nuova registrazione');
          return of(null);
        })
      );
    }

    cleanup$.pipe(
      switchMap(() => this.registerTokenRequest$(buildingId, token)),
      take(1)
    ).subscribe({
      error: () => undefined,
    });
  }

  private registerTokenRequest$(buildingId: string, token: string) {
    return this.http.post(
      `${environment.apiEndpoint}/notifications/register-token`,
      {
        token,
        buildingId
      }
    ).pipe(
      take(1),
      tap(() => {
        const currentUserId = this.requestsService.getCurrentUser()?.id;
        if (currentUserId) {
          this.markRegisteredTokenContext(currentUserId, buildingId, token);
        }
        this.loggerService.info(`Token FCM registrato per l'edificio ${buildingId}`);
      }),
      catchError((err) => {
        this.loggerService.error("Errore nella registrazione del token FCM verso il backend");
        throw err;
      })
    );
  }

  private unregisterTokenRequest$(buildingId: string, token: string, clearStoredContext: boolean) {
    return this.http.post(
      `${environment.apiEndpoint}/notifications/unregister-token`,
      {
        token,
        buildingId
      }
    ).pipe(
      take(1),
      tap(() => {
        if (clearStoredContext) {
          this.clearRegisteredTokenContext(buildingId, token);
        }
        this.loggerService.info(`Token FCM disassociato dall'edificio ${buildingId}`);
      }),
      catchError((err) => {
        this.loggerService.error('Errore nella rimozione dell\'associazione token/building');
        throw err;
      })
    );
  }

  private handleIncomingNotification(payload: Pick<MessagePayload, 'data' | 'notification'> | PushNotificationSchema) {
    this.monitoringPreferences?.setActiveCount(payload.data?.['activeAlertCount'], payload.data?.['buildingId']);
    if (payload.data?.['type'] === 'monitoring_preferences_changed') {
      const buildingId = payload.data['buildingId'];
      const version = Number(payload.data['version']);
      if (typeof buildingId === 'string') {
        this.monitoringPreferences?.changed({ buildingId, version: Number.isSafeInteger(version) ? version : undefined });
      }
      return;
    }
    if (payload.data?.['type'] === 'ping') {
      return;
    }

    const eventType = payload.data?.['eventType'];
    if (!canDisplayAlertEventType(eventType, this.requestsService.getCurrentUser()?.role)) {
      this.loggerService.info(`Notification ignored for non-displayable event type: ${eventType}`);
      return;
    }

    this.loggerService.info(`Notification received: ${payload}`);
    let facilities = payload.data?.['facilities'];
    if (typeof facilities === 'string') {
      try {
        facilities = JSON.parse(facilities);
      } catch {
        this.loggerService.warn('Notification ignored: invalid facilities');
        return;
      }
    }
    const notificationData = {
      ...payload.data,
      facilities,
      title: payload.data?.['title'] ?? ('title' in payload ? payload.title : ('notification' in payload ? payload.notification?.title : undefined)),
      body: payload.data?.['body'] ?? ('body' in payload ? payload.body : ('notification' in payload ? payload.notification?.body : undefined)),
      severity: eventTypeToData[(payload.data as unknown as INotificationData).eventType]?.severity,
      timestamp: new Date(payload.data?.['timestamp'] || new Date())
    } as INotificationData;
    if (!this.sharedService.addAlert(notificationData)) {
      return;
    }
    const notificationTitle = this.getNotificationTitle(payload, notificationData);

    this.toastService.showToast(
      notificationData.eventType === '08'
        ? [notificationTitle, notificationData.body].filter((value) => value !== undefined).join('\n')
        : notificationTitle || '',
      eventTypeToData[notificationData.eventType]?.ionicColor,
      3000,
      {
        plainText: true,
        centerText: true,
        hideIcon: true,
        cssClass: 'app-toast--notification'
      }
    );
    this.loggerService.info(`Notification receive time: ${getTimeDifferenceInMs(payload.data?.['timestamp'] || new Date())}`);
    this.ringtoneService.playRingtone();
  }

  private getStoredFcmToken(): string {
    return localStorage.getItem(this.fcmTokenStorageKey) || '';
  }

  private getRegisteredTokenContext(): { userId: number | null; buildingId: string | null; token: string | null } {
    const storedUserId = localStorage.getItem(this.registeredUserStorageKey);

    return {
      userId: storedUserId ? Number(storedUserId) : null,
      buildingId: localStorage.getItem(this.registeredBuildingStorageKey),
      token: localStorage.getItem(this.registeredTokenStorageKey)
    };
  }

  private markRegisteredTokenContext(userId: number, buildingId: string, token: string) {
    localStorage.setItem(this.registeredUserStorageKey, String(userId));
    localStorage.setItem(this.registeredBuildingStorageKey, buildingId);
    localStorage.setItem(this.registeredTokenStorageKey, token);
  }

  private clearRegisteredTokenContext(buildingId?: string, token?: string, userId?: number) {
    const registeredContext = this.getRegisteredTokenContext();

    if (
      (!userId || registeredContext.userId === userId) &&
      (!buildingId || registeredContext.buildingId === buildingId) &&
      (!token || registeredContext.token === token)
    ) {
      localStorage.removeItem(this.registeredUserStorageKey);
      localStorage.removeItem(this.registeredBuildingStorageKey);
      localStorage.removeItem(this.registeredTokenStorageKey);
    }
  }

  private getNotificationTitle(
    payload: Pick<MessagePayload, 'data' | 'notification'> | PushNotificationSchema,
    notificationData?: INotificationData
  ): string | undefined {
    if (notificationData?.eventType === '08') {
      return notificationData.title;
    }
    const translatedEventTitle = notificationData?.eventType
      ? this.translateService.instant(eventTypeToData[notificationData.eventType]?.translation || '')
      : '';

    if (translatedEventTitle) {
      return translatedEventTitle;
    }

    if ('title' in payload && typeof payload.title === 'string') {
      return payload.title;
    }

    if ('notification' in payload) {
      return payload.notification?.title;
    }

    return undefined;
  }
}
